/* iter55z-b — Poll /forum/unread-count so the ActivityBar community
 * button can render a red dot (with mention count) when new posts
 * have arrived since the user last opened the dock.
 *
 * The "since" cursor is persisted to localStorage; we advance it
 * whenever the dock is opened. Mentions are tracked separately so
 * the pill can highlight in red when the user is @-mentioned.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const CURSOR_KEY = 'pw_chat_dock_last_seen';
const POLL_MS = 30_000;

function readCursor() {
  try {
    const v = window.localStorage.getItem(CURSOR_KEY);
    return v || null;
  } catch { return null; }
}
function writeCursor(iso) {
  try { window.localStorage.setItem(CURSOR_KEY, iso); } catch { /* ignore */ }
}

// iter55z-b code-review follow-up — On the first-ever mount for a
// browser, there is no cursor and the unread endpoint counts every
// non-own post ever posted, so a brand-new user always sees the red
// dot until they open the dock once. Seed the cursor to "now" the
// very first time so the badge only reflects genuinely new activity.
function ensureCursorInitialized() {
  if (!readCursor()) writeCursor(new Date().toISOString());
}

export function useForumUnread({ authed = true } = {}) {
  const [state, setState] = useState({ unread: 0, mentions: 0 });
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!authed) return;
    try {
      const since = readCursor();
      const params = since ? { since } : {};
      const r = await axios.get(`${API}/forum/unread-count`, {
        params, withCredentials: true,
      });
      if (alive.current) setState(r.data);
    } catch {
      /* ignore — likely unauth or offline */
    }
  }, [authed]);

  useEffect(() => {
    alive.current = true;
    if (!authed) return undefined;
    ensureCursorInitialized();
    load();
    const iv = window.setInterval(load, POLL_MS);
    return () => { alive.current = false; window.clearInterval(iv); };
  }, [authed, load]);

  const markRead = useCallback(() => {
    writeCursor(new Date().toISOString());
    setState({ unread: 0, mentions: 0 });
  }, []);

  return { ...state, markRead, refresh: load };
}
